import { useMutation } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';
import { renderSkeletonCards } from '../../../components/SkeletonElements';
import { useAppDataContext } from '../../../contexts/appData';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { useNavigationGuardBlocker } from '../../../hooks/useNavigationGuard';
import { SimpleNewRoute } from '../../../types/route';
import { prepareAllowedPorts } from '../../../utils/port';
import DeleteRouteConfirmationDialog from './DeleteRouteConfirmationDialog';
import RouteElement from './RouteElement';
import { ContractTemplateSubPageContext, ProcessedRoute } from './types';
import { convertSimpleRouteToRoute, processRoute } from './utils';
import useDraftList, { ListElement } from '../../../hooks/useDraftList';
import useFreshDataEnsurer from '../../../hooks/useFreshDataEnsurer';
import { RouteStatus } from '../../../types/core';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';
import LosingChangesConfimationDialog from '../../../components/LosingChangesConfimationDialog';
import RescindRouteConfirmationDialog from './RescindRouteConfirmationDialog';
import AddButtonCard from '../../../components/AddButtonCard';

export const ContractTimetablePage = () => {
  const { contractId, contract, isContractDisabled, routesManagement } =
    useOutletContext<ContractTemplateSubPageContext>();
  const { ports: allPorts } = useAppDataContext();
  const { createContractRoute } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { language, t } = useLanguage();
  const { isOperator } = useCurrentUser();
  const {
    addRouteToContext,
    error: routesError,
    isFetching: isFetchingRoutes,
    isLoading: isLoadingRoutes,
    refetch: refetchRoutes,
    routes: fetchedRoutes,
  } = routesManagement;

  const {
    mutate: applyCreateContractRouteMutation,
    isPending: isCreateContractRoutePending,
    reset: resetCreateContractRouteMutation,
  } = useMutation({
    mutationFn: (newRoute: SimpleNewRoute) =>
      createContractRoute(contractId ?? '', newRoute),
  });

  const [contractDateFrom, contractDateTo] = useMemo<Dayjs[]>(
    () =>
      [contract.dateFrom, contract.dateTo].map(dateString => dayjs(dateString)),
    [contract],
  );

  const {
    allowedPorts,
    defaultArrivalPort,
    defaultDeparturePort,
    allowedPortsMap,
  } = useMemo(
    () => prepareAllowedPorts(allPorts, contract),
    [allPorts, contract],
  );

  const processedRoutes = useMemo<ProcessedRoute[]>(() => {
    if (fetchedRoutes.length === 0) {
      return [];
    }

    const [todayString] = new Date().toISOString().split('T');

    return fetchedRoutes.map<ProcessedRoute>(route =>
      processRoute(route, language, allowedPortsMap, todayString),
    );
  }, [fetchedRoutes, allowedPortsMap, language]);

  const {
    list,
    isReady: isListReady,
    operations,
  } = useDraftList(processedRoutes, !isLoadingRoutes && !isFetchingRoutes);

  const existingRoutes = useMemo(
    () =>
      list
        .map(item => (item.isDraft ? null : item.data))
        .filter(Boolean) as ProcessedRoute[],
    [list],
  );

  const [timetableListElementToDelete, setTimetableListElementToDelete] =
    useState<{
      element: ListElement<ProcessedRoute>;
      isRescinding: boolean;
    } | null>(null);

  useFreshDataEnsurer(isFetchingRoutes, refetchRoutes);

  useNavigationGuardBlocker(() => Boolean(list.find(item => item.isDraft)));

  useEffect(() => {
    if (routesError) {
      enqueueSnackbar({ variant: 'error', message: routesError.message });
    }
  }, [enqueueSnackbar, routesError]);

  const showAddButton = !isOperator && !isContractDisabled;

  return (
    <div className="flex flex-col gap-4">
      {isLoadingRoutes ? (
        renderSkeletonCards(3)
      ) : (
        <>
          {showAddButton && (
            <AddButtonCard
              disabled={!isListReady}
              onClick={() => operations.addDraftElement()}
            />
          )}
          {list.map(item =>
            item.isDraft ? (
              <RouteElement
                key={item.id}
                route={null}
                copiedRoute={item.metadata}
                existingRoutes={existingRoutes}
                isLoading={isFetchingRoutes || isCreateContractRoutePending}
                allowedPorts={allowedPorts}
                contractDateFrom={contractDateFrom}
                contractDateTo={contractDateTo}
                defaultArrivalPort={defaultArrivalPort}
                defaultDeparturePort={defaultDeparturePort}
                onDelete={hasPendingChanges => {
                  if (hasPendingChanges) {
                    setTimetableListElementToDelete({
                      element: item,
                      isRescinding: false,
                    });
                  } else {
                    operations.removeElement(item);
                  }
                }}
                onSave={newSimpleRoute => {
                  applyCreateContractRouteMutation(newSimpleRoute, {
                    onSuccess: simpleRoute => {
                      const newRoute = convertSimpleRouteToRoute(
                        simpleRoute,
                        allowedPortsMap,
                      );
                      enqueueSnackbar({
                        variant: 'success',
                        message: t('content.contract-route-creation-success'),
                      });
                      operations.promoteDraftToPersistentElement(
                        item.id,
                        processRoute(newRoute, language, allowedPortsMap),
                      );
                      addRouteToContext(newRoute);
                    },
                    onError: error => {
                      enqueueSnackbar({
                        variant: 'error',
                        message: getErrorMessage(
                          error,
                          t('content.contract-route-creation-error'),
                        ),
                      });
                      resetCreateContractRouteMutation();
                    },
                  });
                }}
              />
            ) : (
              <RouteElement
                key={item.data.id}
                route={item.data}
                existingRoutes={existingRoutes}
                isLoading={isFetchingRoutes || isCreateContractRoutePending}
                allowedPorts={allowedPorts}
                contractDateFrom={contractDateFrom}
                contractDateTo={contractDateTo}
                defaultArrivalPort={defaultArrivalPort}
                defaultDeparturePort={defaultDeparturePort}
                areActionsDisabled={isContractDisabled || isOperator}
                onRescind={() => {
                  setTimetableListElementToDelete({
                    element: item,
                    isRescinding: true,
                  });
                }}
                onDelete={() => {
                  setTimetableListElementToDelete({
                    element: item,
                    isRescinding: false,
                  });
                }}
                onCopyToNextYear={() =>
                  operations.addDraftElement({
                    before: item,
                    metadata: {
                      ...item.data,
                      dateFrom: item.data.dateFrom.add(1, 'year'),
                      dateTo: item.data.dateTo.add(1, 'year'),
                    },
                  })
                }
              />
            ),
          )}
        </>
      )}
      <LosingChangesConfimationDialog
        isOpen={Boolean(
          timetableListElementToDelete &&
            timetableListElementToDelete.element.isDraft,
        )}
        onClose={() => setTimetableListElementToDelete(null)}
        onConfirm={() => {
          if (timetableListElementToDelete?.element.isDraft) {
            operations.removeElement(timetableListElementToDelete.element);
            setTimetableListElementToDelete(null);
          }
        }}
      />
      <DeleteRouteConfirmationDialog
        contractId={contract.id}
        route={
          timetableListElementToDelete &&
          !timetableListElementToDelete.element.isDraft &&
          !timetableListElementToDelete.isRescinding
            ? timetableListElementToDelete.element.data
            : null
        }
        isLoading={isFetchingRoutes || isCreateContractRoutePending}
        onClose={() => {
          setTimetableListElementToDelete(null);
        }}
        onConfirm={() => {
          if (timetableListElementToDelete) {
            operations.removeElement(timetableListElementToDelete.element);
          }
        }}
      />
      <RescindRouteConfirmationDialog
        contractId={contract.id}
        route={
          timetableListElementToDelete &&
          !timetableListElementToDelete.element.isDraft &&
          timetableListElementToDelete.isRescinding
            ? timetableListElementToDelete.element.data
            : null
        }
        isLoading={isFetchingRoutes || isCreateContractRoutePending}
        onClose={() => {
          setTimetableListElementToDelete(null);
        }}
        onConfirm={() => {
          if (
            timetableListElementToDelete &&
            !timetableListElementToDelete.element.isDraft
          ) {
            operations.updatePersistentElement(
              timetableListElementToDelete.element.data,
              route => ({
                ...route,
                routeStatus: RouteStatus.Disabled,
                dateTo: dayjs(),
              }),
            );
          }
        }}
      />
    </div>
  );
};
